import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { compact } from 'lodash';
import {
  faBinoculars,
  faColumns,
  faHome,
} from '@fortawesome/free-solid-svg-icons';
import { faBooks } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@ebsco-ui/ebsco-ui';

import { FeatureFlagDisplay, LinkMenuItem } from '@app/components';
import { useFeatureFlag, useHoldsAndCheckoutsPageConfig } from '@app/hooks';
import { NavigationDropdownItem, sharedMessages } from '@app/translations';
import { Feature, Route, VoidFn } from '@app/constants';

import { CatalogLink } from '../CatalogLink';
import { LibraryResourcesDropdown } from './LibraryResourcesDropdown';
import { NavigationDropdown } from './NavigationDropdown';

import css from './NavigationPanel.module.scss';

interface NavigationPanelProps {
  isMobile?: boolean;
  toggleDrawer?: VoidFn;
}

export const NavigationPanel: FC<NavigationPanelProps> = ({
  isMobile,
  toggleDrawer,
}) => {
  const { $t } = useIntl();
  const { isActive: isHoldsAndCheckoutsFlagActive } = useFeatureFlag({
    name: Feature.holdsAndCheckouts,
    isActiveIfNotFound: true,
  });
  const {
    holdsAndCheckoutsRoute,
    selectConfiguredOption: selectConfiguredHoldsAndCheckoutsOption,
  } = useHoldsAndCheckoutsPageConfig();

  const handleCloseMobileMenu = () => toggleDrawer?.();

  const commonNavigationDropdownProps = { isMobile, toggleDrawer };
  const commonLinkMenuItemProps = {
    className: css.link,
    activeClassName: css.isActive,
    onClick: handleCloseMobileMenu,
  };
  const browseDropdownLinks = useMemo(
    () => [
      {
        flag: { name: Feature.courseReserves },
        label: $t(sharedMessages.courseReserves),
        href: Route.courseReserves,
      },
      {
        flag: { name: Feature.callNumbers },
        label: $t({
          defaultMessage: 'Call numbers',
          id: 'navigation.callNumbers',
        }),
        href: Route.callNumbers,
      },
    ],
    [$t]
  );
  const dashboardDropdownLinks = useMemo(
    () =>
      compact([
        { label: $t(sharedMessages.projects), href: Route.projects },
        { label: $t(sharedMessages.saved), href: Route.saved },
        {
          label: $t(sharedMessages.recentActivity),
          href: Route.recentActivity,
        },
        isHoldsAndCheckoutsFlagActive && {
          label: selectConfiguredHoldsAndCheckoutsOption(
            $t(sharedMessages.requestsAndLoans),
            $t(sharedMessages.holdsAndCheckouts)
          ),
          href: holdsAndCheckoutsRoute,
        },
      ]),
    [
      $t,
      holdsAndCheckoutsRoute,
      isHoldsAndCheckoutsFlagActive,
      selectConfiguredHoldsAndCheckoutsOption,
    ]
  );

  return (
    <nav
      className={css.navigation}
      aria-label={$t({ id: 'navigation.main', defaultMessage: 'Main' })}
    >
      {isMobile && (
        <div className={css.closeButtonWrapper}>
          <Button
            styleType="flat-icon"
            className={css.closeButton}
            aria-label={$t(sharedMessages.close)}
            onClick={handleCloseMobileMenu}
          >
            <FontAwesomeIcon className="fa-lg" icon={faTimes} />
          </Button>
        </div>
      )}
      <ul className={css.navigationLinks}>
        {isMobile && <CatalogLink />}
        <LinkMenuItem
          to={Route.search}
          icon={faHome}
          {...commonLinkMenuItemProps}
        >
          {$t({ id: 'navigation.catalogHome', defaultMessage: 'Catalog home' })}
        </LinkMenuItem>
        <FeatureFlagDisplay
          flagToMatch={{ name: Feature.LCDashboard }}
          renderOn={() => (
            <NavigationDropdown
              name={NavigationDropdownItem.dashboard}
              icon={faColumns}
              links={dashboardDropdownLinks}
              {...commonNavigationDropdownProps}
            />
          )}
          renderOff={() => (
            <FeatureFlagDisplay
              flagToMatch={{
                name: Feature.holdsAndCheckouts,
                isActiveIfNotFound: true,
              }}
            >
              <LinkMenuItem
                to={holdsAndCheckoutsRoute}
                icon={faBooks}
                {...commonLinkMenuItemProps}
              >
                {$t({
                  id: 'navigation.myBookshelf',
                  defaultMessage: 'My bookshelf',
                })}
              </LinkMenuItem>
            </FeatureFlagDisplay>
          )}
        />
        <NavigationDropdown
          name={NavigationDropdownItem.browse}
          icon={faBinoculars}
          links={browseDropdownLinks}
          {...commonNavigationDropdownProps}
        />
        <LibraryResourcesDropdown
          commonNavigationDropdownProps={commonNavigationDropdownProps}
        />
      </ul>
    </nav>
  );
};
